import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { Package, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react'
import { useCatalog } from '@/hooks/data/useBookings'
import {
  useCreatePackage,
  useUpdatePackage,
  useDeletePackage,
} from '@/hooks/data/useCatalogAdmin'
import type { ServicePackage, CatalogPackageInput } from '@/types/domain'
import EditModal from '@/components/crud/EditModal'
import { useDeleteConfirm } from '@/components/crud/DeleteConfirm'

function PackagesPage() {
  const { data: catalog, isLoading, isError, error, isFetching, refetch } = useCatalog()
  const packages = catalog?.packages ?? []
  const create = useCreatePackage()
  const update = useUpdatePackage()
  const del = useDeletePackage()
  const confirmDelete = useDeleteConfirm()
  const [creating, setCreating] = useState(false)
  const [q, setQ] = useState('')
  const [editing, setEditing] = useState<ServicePackage | null>(null)

  const rows = packages.filter((p) => !q || p.name.toLowerCase().includes(q.toLowerCase()))

  const fields = [
    { key: 'code', label: 'Code', required: true, placeholder: 'DEEP_CLEAN' },
    { key: 'name', label: 'Name', required: true },
    { key: 'description', label: 'Description', type: 'textarea' as const, required: true },
    { key: 'basePrice', label: 'Base price', type: 'number' as const, required: true },
    { key: 'baseDurationMinutes', label: 'Duration (minutes)', type: 'number' as const, required: true },
    {
      key: 'serviceType',
      label: 'Service type',
      type: 'select' as const,
      required: true,
      options: [
        { value: '', label: 'Select service type…' },
        { value: 'Regular', label: 'Regular' },
        { value: 'Deep', label: 'Deep' },
        { value: 'MoveOut', label: 'Move Out' },
      ],
    },
    {
      key: 'inclusions',
      label: 'Inclusions',
      type: 'textarea' as const,
      placeholder: 'One inclusion per line',
    },
  ]

  const toPackageInput = (values: Record<string, any>): CatalogPackageInput => ({
    code: values.code,
    name: values.name,
    description: values.description,
    basePrice: Number(values.basePrice),
    baseDurationMinutes: Number(values.baseDurationMinutes),
    serviceType: values.serviceType,
    inclusions: String(values.inclusions ?? '')
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean),
  })

  return (
    <div className="p-2 md:p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <Package className="w-6 h-6" /> Service Packages
          </h1>
          <p className="text-gray-600 dark:text-gray-400">Manage cleaning service packages (CatalogAdminController).</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => refetch()} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
          <button onClick={() => setCreating(true)} className="flex items-center gap-1 px-3 py-2 rounded-lg bg-blue-600 text-white text-sm">
            <Plus className="w-4 h-4" /> New Package
          </button>
        </div>
      </div>

      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search packages…"
        className="w-full md:w-72 rounded-lg border border-gray-200 dark:border-gray-700 bg-transparent px-3 py-2 text-sm"
      />

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {isLoading ? (
          <p className="p-6 text-gray-500">Loading packages…</p>
        ) : isError ? (
          <p className="p-6 text-red-600">Failed to load packages: {(error as Error)?.message}.</p>
        ) : rows.length === 0 ? (
          <p className="p-6 text-gray-500">No packages found.</p>
        ) : (
          <table className="min-w-full text-left text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/40 text-gray-500">
              <tr>
                <th className="px-4 py-2">Package</th>
                <th className="px-4 py-2">Code</th>
                <th className="px-4 py-2">Price</th>
                <th className="px-4 py-2">Duration</th>
                <th className="px-4 py-2">Inclusions</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} className="border-t border-gray-100 dark:border-gray-700">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-800 dark:text-gray-100">{p.name}</div>
                    {p.description && <div className="text-xs text-gray-500 line-clamp-1">{p.description}</div>}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">{p.code}</td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300">${p.basePrice.toFixed(2)}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">{p.baseDurationMinutes} min</td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {p.inclusions.length} item{p.inclusions.length === 1 ? '' : 's'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => setEditing(p)} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500" title="Edit package">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() =>
                          confirmDelete(p.name, async () => {
                            await del.mutateAsync(p.id)
                          })
                        }
                        className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500"
                        title="Delete package"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <EditModal
        isOpen={creating}
        onClose={() => setCreating(false)}
        title="New Package"
        fields={fields}
        submitLabel="Create"
        onSubmit={async (values) => {
          await create.mutateAsync(toPackageInput(values))
        }}
      />

      <EditModal
        isOpen={!!editing}
        onClose={() => setEditing(null)}
        title="Edit Package"
        description={editing?.name}
        fields={fields}
        initialValues={
          editing
            ? {
                code: editing.code,
                name: editing.name,
                description: editing.description,
                basePrice: editing.basePrice,
                baseDurationMinutes: editing.baseDurationMinutes,
                // serviceType isn't in the public catalog read (marketing-subset projection) —
                // left blank so the admin must consciously pick it rather than silently
                // overwriting it with a wrong default on save.
                inclusions: editing.inclusions.join('\n'),
              }
            : {}
        }
        onSubmit={async (values) => {
          const id = editing?.id
          if (!id) return
          await update.mutateAsync({ id, body: toPackageInput(values) })
        }}
      />
    </div>
  )
}

export const Route = createFileRoute('/(admin)/packages/')({
  component: PackagesPage,
})
